import React, { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { toast } from '@/components/ui/use-toast'
import { getGmailSecondaryOAuthUrl, buildOutlookSecondaryStartUrl } from '../services/mailboxesApi'

type Props = {
  workspaceId: string
  userId: string
  onClose: () => void
}

const GmailIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#4285f4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#34a853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#fbbc05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#ea4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
)

const OutlookIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#0A2767" d="M44 18.5v16.9a2.1 2.1 0 0 1-2.1 2.1H28V22.1L30 20l12.6-6.2A1.9 1.9 0 0 1 44 15.4v3.1Z" />
    <path fill="#0364B8" d="M28 6H9.1A2.1 2.1 0 0 0 7 8.1v8.3l10.5 6.2L28 28V6Z" />
    <path fill="#0078D4" d="M7 16.4v18.5A2.1 2.1 0 0 0 9.1 37H28V22.1L7 16.4Z" />
    <path fill="#50D9FF" d="M28 22.1V37h13.9a2.1 2.1 0 0 0 2.1-2.1V18.5L28 22.1Z" />
    <circle cx="17.5" cy="22.5" r="6.5" fill="#fff" />
    <path fill="#0078D4" d="M17.5 17.5a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 8.3a3.3 3.3 0 1 1 0-6.6 3.3 3.3 0 0 1 0 6.6z" />
  </svg>
)

const AddMailboxDialog: React.FC<Props> = ({ workspaceId, userId, onClose }) => {
  const { currentTheme: t } = useTheme()
  const [loading, setLoading] = useState<'gmail' | 'outlook' | null>(null)

  const connectGmail = async () => {
    setLoading('gmail')
    try {
      const url = await getGmailSecondaryOAuthUrl(workspaceId)
      // Leaves the Sales → Mailboxes tab via full-page nav; Google redirects
      // back to /dashboard/sales?tab=mailboxes&mailbox=connected on success
      // (source=campaigns in state is the legacy name of that origin).
      window.location.href = url
    } catch (err) {
      setLoading(null)
      toast({
        variant: 'destructive',
        title: "Couldn't start Gmail connect",
        description: err instanceof Error ? err.message : 'Try again in a moment.',
      })
    }
  }

  const connectOutlook = () => {
    setLoading('outlook')
    window.location.href = buildOutlookSecondaryStartUrl(workspaceId, userId)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={loading ? undefined : onClose} />
      <div
        className="relative z-10 w-full max-w-md mx-4 rounded-xl p-6 shadow-xl"
        style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}` }}
      >
        <h3 className="text-sm font-semibold mb-1" style={{ color: t.text }}>Add a mailbox</h3>
        <p className="text-xs mb-5 leading-relaxed" style={{ color: t.mutedText }}>
          Choose a provider. You&apos;ll be sent to the provider to sign in and grant Zeph permission to send email.
        </p>

        <div className="flex flex-col gap-2.5">
          <button
            type="button"
            onClick={connectGmail}
            disabled={!!loading}
            className="flex items-center gap-3 p-3.5 rounded-lg text-left transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: t.hoverBackground, border: `1px solid ${t.border}`, color: t.text }}
          >
            <div
              className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
              style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
            >
              {loading === 'gmail' ? <Loader2 size={16} className="animate-spin" /> : <GmailIcon />}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold" style={{ color: t.text }}>Gmail</p>
              <p className="text-[11.5px] mt-0.5" style={{ color: t.mutedText }}>Connect a Google account you want Zeph to send from.</p>
            </div>
          </button>

          <button
            type="button"
            onClick={connectOutlook}
            disabled={!!loading}
            className="flex items-center gap-3 p-3.5 rounded-lg text-left transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: t.hoverBackground, border: `1px solid ${t.border}`, color: t.text }}
          >
            <div
              className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
              style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
            >
              {loading === 'outlook' ? <Loader2 size={16} className="animate-spin" /> : <OutlookIcon />}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold" style={{ color: t.text }}>Outlook</p>
              <p className="text-[11.5px] mt-0.5" style={{ color: t.mutedText }}>Connect a Microsoft account you want Zeph to send from.</p>
            </div>
          </button>
        </div>

        <div className="flex justify-end mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={!!loading}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-80 disabled:opacity-50"
            style={{ backgroundColor: t.hoverBackground, border: `1px solid ${t.border}`, color: t.text }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

export default AddMailboxDialog
